import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { HttpGateway } from '@core/services/http.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import type { ActionFailurePm, ActionResponsePm } from '@core/feedback/feedback.packing'
import { MessagePacking } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import { FreshnessRepository } from '@core/infra/freshness/freshness.repository'

export type LoadAuditFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

export type AuditEntryPm = {
  readonly entryId: string
  readonly at: string
  readonly actorEmail: string
  readonly action: string
  readonly targetKind: string
  readonly targetId: string
  readonly targetLabel: string
  readonly detail: string
}

/**
 * `action` and `targetKind` are plain strings rather than a union, on purpose:
 * an entry written by an older build may name an action this bundle has never
 * heard of, and a historical record is not something the reader gets to reject.
 * The Presenter falls back to showing the raw value.
 */
const AuditListDtoSchema = z.array(
  z.object({
    entryId: z.string(),
    at: z.string(),
    actorEmail: z.string(),
    action: z.string(),
    target: z.object({ kind: z.string(), id: z.string(), label: z.string() }),
    detail: z.string(),
  }),
)

export const LOAD_AUDIT_OPERATION = defineOperation<null, LoadAuditFailure>({
  id: operationId('admin.audit.load'),
  key: () => operationKey('admin-audit'),
  groups: () => [operationGroup('session'), operationGroup('admin-audit')],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable', retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/** How many entries the page asks for; the backend clamps its own maximum. */
/**
 * How many entries one read asks for. Exported because the page has to *say* so:
 * the trail is append-only and unbounded, so a silently truncated list invites
 * reading "nothing else happened" into "nothing else is on this screen".
 */
export const AUDIT_WINDOW = 100

/** Read-only by construction — the log is append-only, and nothing writes it from here. */
@injectable()
export class AdminAuditRepository implements UserScopedState {
  auditListPm: readonly AuditEntryPm[] | null = null

  private readonly dataGateway: HttpGateway
  private readonly freshnessRepository: FreshnessRepository
  private readonly operationOwner: OperationOwner
  private readonly loadOperation: OperationHandle<null, LoadAuditFailure>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
    @inject(FreshnessRepository) freshnessRepository: FreshnessRepository,
  ) {
    this.dataGateway = dataGateway
    this.freshnessRepository = freshnessRepository
    this.operationOwner = operationRepository.createOwner({ id: 'admin-audit.repository', lifetime: 'session' })
    this.loadOperation = this.operationOwner.register(LOAD_AUDIT_OPERATION)

    makeObservable(this, { auditListPm: observable.ref })
  }

  get isLoaded(): boolean {
    return this.auditListPm !== null
  }

  loadIfNeeded = async (): Promise<void> => {
    if (this.isLoaded && this.freshnessRepository.isFresh('admin.audit')) return
    await this.load()
  }

  get loadState(): ReadonlyOperationState<LoadAuditFailure> {
    return this.loadOperation.stateFor(null)
  }

  load = async (): Promise<ActionResponsePm> => {
    const result = await this.loadOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get(`/admin/audit?limit=${AUDIT_WINDOW}`, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = AuditListDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        return operationSucceeded(
          Object.freeze(
            parsed.data.map((dto): AuditEntryPm =>
              Object.freeze({
                entryId: dto.entryId,
                at: dto.at,
                actorEmail: dto.actorEmail,
                action: dto.action,
                targetKind: dto.target.kind,
                targetId: dto.target.id,
                targetLabel: dto.target.label,
                detail: dto.detail,
              }),
            ),
          ),
        )
      },
      {
        succeeded: (entries) => {
          this.auditListPm = entries
          this.freshnessRepository.markLoaded('admin.audit')
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.auditListPm = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
